import { Component } from '@angular/core';
import { Router } from '@angular/router';
import {
  GroupContextService,
  hd,
  TicketContextService,
  GlobalEventBusService,
} from '@nx-fe/services';
import { XsI18nPipe } from '@nx-fe/pipes';
import { SspRestApiService } from 'sspapp/services/sspRestApiService';
import { GlobalEvents } from 'sspapp/core/global-event-constants';
import { Ticket } from '../../ticket.model';

@Component({
  selector: 'ssp-ticket-list',
  templateUrl: './ticket-list.component.html',
  styleUrls: ['./ticket-list.component.scss'],
})
export class TicketListComponent {

  public loadedTickets: Ticket[] = [];
  public currentFilter = '';

  private totalTickets = 0;
  private pageIndex = 0;
  private pageSize = 5;

  private filterList: hd.IInfoGraphicFilterList;
  private isTicketResultLoaded = true;
  private currentPagingLabel = '';
  private ticketCreatedUnregister: Function;
  /** Determines if the currently logged-in user can create tickets */
  public canCreateTickets: boolean = false;

  constructor(
    private router: Router,
    private xsI18n: XsI18nPipe,
    private ticketContextService: TicketContextService,
    private groupContextService: GroupContextService,
    private sspRestApiService: SspRestApiService,
    private globalEventBusService: GlobalEventBusService,
  ) {

    /**
     * Define the built-in list of selectable filters
     */
    this.filterList = {
      unresolved: {
        id: 'unresolved',
        filter: 'status:[draft,resolved]:not',
        caption: this.xsI18n.transform('ticketCardList_Filter_Unresolved_Caption'),
      },
      resolved: {
        id: 'resolved',
        filter: 'status:resolved',
        caption: this.xsI18n.transform('ticketCardList_Filter_Resolved_Caption'),
      },
      all: {
        id: 'all',
        filter: 'status:[draft]:not',
        caption: this.xsI18n.transform('ticketCardList_Filter_AllIncidents_Caption'),
      },
    };

    // The page will load with the default filter: unresolved
    this.setCurrentFilter('unresolved', 0);
    this.canCreateTickets = !this.ticketContextService.isLicenceExpired;


    this.globalEventBusService.register(GlobalEvents.submitTicket, (ticket: hd.ITicketModel) => {
      this.setCurrentFilter('unresolved', 0).then(() => {
        this.router.navigate([
          'home/ticket', ticket.ticketId, true
        ]);
      });
    });
  }

  public $onDestroy = () => {
    if (this.ticketCreatedUnregister) {
      this.ticketCreatedUnregister();
    }
  };


  /**
   * Check if the ticket item is selected
   */
  public isTicketSelected = (ticket: hd.ITicketModel): boolean => {
    return this.ticketContextService.ticket && this.ticketContextService.ticket.ticketId === ticket.ticketId;
  };

  /**
   * Increments the page index to the next page (if possible) and reloads the tickets
   */
  public incrementPage() {
    if ((this.pageIndex + 1) * this.pageSize < this.totalTickets) {
      this.currentPagingLabel = this.getPageLabel();
      this.pageIndex++;
      this.loadTickets();
    }
  }

  /**
   * Decrements the page index to the previous page (if possible) and reloads the tickets
   */
  public decrementPage() {
    if (this.pageIndex > 0) {
      this.currentPagingLabel = this.getPageLabel();
      this.pageIndex--;
      this.loadTickets();
    }
  }
  /**
   * Gets the page label for the tickets currently being displayed
   */
  public getPageLabel = (): string => {
    if (this.loadedTickets) {
      if (this.isTicketResultLoaded) {
        const from = this.pageIndex * this.pageSize + 1;
        const to = this.pageIndex * this.pageSize + this.loadedTickets.length;
        const total = this.totalTickets;
        return this.xsI18n.transform('ticketCardList_PageLabel_Caption', from, to, total);
      } else {
        return this.currentPagingLabel;
      }
    }
  };

  /**
   * Gets a value indicating whether the pager should be shown or not
   */
  public showPager = (): boolean => {
    return !!this.loadedTickets && this.totalTickets > this.pageSize;
  };

  /**
   * Hide the pager and move it to first page and applies the specified ticket list filter.
   *
   * @param filter ID for the filter to be applied.
   * @param page number for the page pageIndex should be set to.
   */
  public setCurrentFilter = (filter: string, page = 0): Promise<any> => {
    this.currentFilter = filter;
    this.pageIndex = page;
    return this.loadTickets();
  };

  public getSelectOptions = () => {
    return Object.values(this.filterList);
  };

  private loadTickets(): Promise<any> {
    return this.sspRestApiService
      .getTicketData(this.currentFilter, this.pageSize * this.pageIndex, this.pageSize)
      .toPromise()
      .then((searchResults: any) => {
        this.loadedTickets = searchResults;
        if (this.loadedTickets.length != 0){
          this.totalTickets = searchResults[0].totalTickets;
        } else {
          this.totalTickets = 0;
        }


      });

  }

  private selectTicket = (ticketId: string) => {
    this.globalEventBusService.broadcast(GlobalEvents.selectedTicket, {ticketId});
    this.router.navigate([
      'home/ticket', ticketId
    ]);
  };
}
